import { useNavigate } from 'react-router-dom';
import { IconButton, Stack, Text } from '@chakra-ui/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { LuChevronDown } from 'react-icons/lu';
import { Button } from '@/components/ui/button';
import { MenuContent, MenuItem, MenuRoot, MenuTrigger } from '@/components/ui/menu';
import { Tooltip } from '@/components/ui/tooltip';
import type { NavStage } from './navConfig';
import type { Workspace } from './useWorkspaceNav';

interface ProductSwitcherProps {
    stage: NavStage;
    homes: Workspace[];
    groups: Workspace[];
    /** The icon rail has no room for the word, so it shows M / E instead. */
    collapsed?: boolean;
}

/**
 * Manage or Explore — the two products, one above the sections.
 *
 * Distinct from the home switcher: this picks *which product you are using*,
 * where that one picks which home you are looking at. Conflating them is what
 * made the old workspace menu list homes and groups under one chevron, so
 * switching product and switching home looked like the same act.
 */
export function ProductSwitcher({ stage, homes, groups, collapsed = false }: ProductSwitcherProps) {
    const intl = useIntl();
    const navigate = useNavigate();

    const label = intl.formatMessage(
        stage === 'manage'
            ? { id: '6e2c8b41-0d75-4396-a1f8-3b5c9e0d7462', defaultMessage: 'Manage' }
            : { id: '8a95f603-2c17-4e84-b0d6-71f3a8c529b0', defaultMessage: 'Explore' },
    );

    // Land on the workspace the member last used where we can. With none, the
    // destination is the place that creates one.
    const goManage = () => navigate(homes[0]?.root ?? '/onboarding');
    const goExplore = () => navigate(groups[0]?.root ?? '/groups');

    /*
     * Stock `Button` at the smallest step Chakra ships. The recipe owns the
     * height, the horizontal padding, the gap and the chevron's size, so the
     * switcher stays a sibling of every other button in the app instead of a
     * hand-measured pill that drifts from them.
     *
     * `outline` draws the design's gold hairline from the palette. The one
     * addition is the wash behind it — the variant is border-only, and the
     * design's pill is a tint *and* a border.
     */
    const trigger = collapsed ? (
        <IconButton
            size="2xs"
            variant="outline"
            colorPalette="gold"
            bg="gold.washMuted"
            fontWeight="bold"
            aria-label={label}
            mx="auto"
            _hover={{ bg: 'gold.wash' }}
        >
            <Tooltip content={label} positioning={{ placement: 'right' }} openDelay={200}>
                <span>{label.slice(0, 1)}</span>
            </Tooltip>
        </IconButton>
    ) : (
        <Button
            size="2xs"
            variant="outline"
            colorPalette="gold"
            bg="gold.washMuted"
            borderRadius="full"
            fontWeight="bold"
            fontSize="2xs"
            textTransform="uppercase"
            _hover={{ bg: 'gold.wash' }}
        >
            {label}
            <LuChevronDown />
        </Button>
    );

    return (
        <MenuRoot>
            <MenuTrigger asChild>{trigger}</MenuTrigger>
            <MenuContent minW="48">
                <MenuItem value="manage" onClick={goManage} cursor="button">
                    <Stack gap={0.5} align="flex-start">
                        <Text fontSize="sm" fontWeight="semibold" color="fg">
                            <FormattedMessage id="6e2c8b41-0d75-4396-a1f8-3b5c9e0d7462" defaultMessage="Manage" />
                        </Text>
                        <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                            <FormattedMessage
                                id="7b0c5e42-9a31-4d68-b5f7-2e8c1a4d0369"
                                defaultMessage="Run the homes you own"
                            />
                        </Text>
                    </Stack>
                </MenuItem>
                <MenuItem value="explore" onClick={goExplore} cursor="button">
                    <Stack gap={0.5} align="flex-start">
                        <Text fontSize="sm" fontWeight="semibold" color="fg">
                            <FormattedMessage id="8a95f603-2c17-4e84-b0d6-71f3a8c529b0" defaultMessage="Explore" />
                        </Text>
                        <Text fontSize="xs" fontWeight="medium" color="fg.muted">
                            <FormattedMessage
                                id="0e94b7a3-6c58-4f12-8d0b-3a7e5c916d24"
                                defaultMessage="Find a home to co-own"
                            />
                        </Text>
                    </Stack>
                </MenuItem>
            </MenuContent>
        </MenuRoot>
    );
}
